import { Divider } from "@msuite/ui/divider";
import { FormCheckbox, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { type AsyncModalController, ConfirmModal } from "@msuite/ui/modal";
import { useForm, useWatch } from "react-hook-form";
import { CONFIRM_DELETE_PROJECT } from "@/sites/projects/project/panels/project-settings/project-settings-general/shared";

/** Props Interface */
interface DeleteProjectModalProps {
	controller: AsyncModalController<string>;
}

export function DeleteProjectModal({ controller }: DeleteProjectModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		defaultValues: {
			confirm: "",
			iDoUnderstandTheConsequences: false,
		},
	});
	const currentConfirmValue = useWatch({ control: methods.control, name: "confirm" });
	const iDoUnderstandTheConsequences = useWatch({
		control: methods.control,
		name: "iDoUnderstandTheConsequences",
	});

	/** Render */
	return (
		<ConfirmModal
			controller={controller}
			message={t("projects.delete_project_confirm_message")}
			confirmIsDisabled={
				currentConfirmValue !== CONFIRM_DELETE_PROJECT || !iDoUnderstandTheConsequences
			}
			onResolve={() => {
				const value = methods.getValues("confirm");
				methods.reset();
				return value;
			}}
			data-cy="delete-project-modal"
		>
			<TextInput
				control={methods.control}
				name="confirm"
				label="Confirm"
				placeholder={CONFIRM_DELETE_PROJECT}
				helperText={`Type "${CONFIRM_DELETE_PROJECT}" to confirm`}
				isRequired
				data-cy="delete-project-confirm-input"
			/>
			<Divider variant="modal" />
			<FormCheckbox
				control={methods.control}
				name="iDoUnderstandTheConsequences"
				data-cy="delete-project-understand-checkbox"
			>
				{t("general.i_do_understand_the_consequences")}
			</FormCheckbox>
		</ConfirmModal>
	);
}
